import React, { useMemo, useState } from 'react';
import {
  PencilSquareIcon,
  TrashIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useBillingStore } from '../store/billingStore';
import { useBilling } from '../hooks/useBilling';
import type { TimeEntry } from '../types/billing.types';
import type { BillingStatus } from '../types/billing.types';
import { BILLING_STATUS_LABELS, BILLING_STATUS_TRANSITIONS } from '../types/billing.types';
import BillingStatusBadge from './BillingStatusBadge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../../../components/ui/dropdown-menu';
import ConfirmModal from './ConfirmModal';

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const formatDuration = (hours: number): string => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const TimeEntriesView: React.FC = () => {
  const store = useBillingStore();
  const {
    deleteTimeEntry,
    startTimer,
    pauseTimer,
    stopTimer,
    bulkApproveTimeEntries,
    transitionTimeEntryBillingStatus,
    bulkTransitionTimeEntryBillingStatus,
  } = useBilling();

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleEdit = (entry: TimeEntry) => {
    store.openDrawer('time-entry', 'edit', entry.time_entry_id);
  };

  const handleDelete = (id: string) => {
    setDeleteConfirm(id);
  };

  const confirmDelete = async () => {
    if (deleteConfirm) {
      await deleteTimeEntry(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  const handleBulkApprove = async () => {
    if (store.selectedTimeEntryIds.length === 0) return;
    await bulkApproveTimeEntries(store.selectedTimeEntryIds);
  };

  const handleBulkStatusTransition = async (newStatus: BillingStatus) => {
    if (store.selectedTimeEntryIds.length === 0) return;
    await bulkTransitionTimeEntryBillingStatus(store.selectedTimeEntryIds, newStatus);
  };

  // Compute common valid transitions for all selected entries
  const commonBulkTransitions = useMemo(() => {
    if (store.selectedTimeEntryIds.length === 0) return [] as BillingStatus[];
    const selectedEntries = store.timeEntries.filter((e) =>
      store.selectedTimeEntryIds.includes(e.time_entry_id)
    );
    if (selectedEntries.length === 0) return [] as BillingStatus[];
    const sets = selectedEntries.map((e) => new Set(BILLING_STATUS_TRANSITIONS[e.billing_status]));
    const intersection = [...sets[0]].filter((s) => sets.every((set) => set.has(s)));
    return intersection;
  }, [store.selectedTimeEntryIds, store.timeEntries]);

  if (store.timeEntries.length === 0 && !store.loading) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500 dark:text-gray-400 text-lg">No time entries found.</p>
        <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
          Create a new time entry to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Bulk actions */}
      {store.selectedTimeEntryIds.length > 0 && (
        <div className="flex items-center gap-3 bg-lex-accent-soft border border-lex-accent rounded-lg px-4 py-2">
          <span className="text-sm text-lex-accent">
            {store.selectedTimeEntryIds.length} selected
          </span>
          <button
            onClick={handleBulkApprove}
            className="flex items-center gap-1 text-sm px-3 py-1 bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover transition-colors"
          >
            <CheckCircleIcon className="h-4 w-4" />
            Approve Selected
          </button>
          {commonBulkTransitions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger>
                <span className="text-sm">Change Status</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {commonBulkTransitions.filter((s) => s !== 'written_off').map((target) => (
                  <DropdownMenuItem key={target} onClick={() => handleBulkStatusTransition(target)}>
                    {BILLING_STATUS_LABELS[target]}
                  </DropdownMenuItem>
                ))}
                {commonBulkTransitions.includes('written_off') && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleBulkStatusTransition('written_off')}
                      className="text-red-600 dark:text-red-400"
                    >
                      {BILLING_STATUS_LABELS.written_off}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button
            onClick={store.clearSelections}
            className="text-sm text-lex-accent hover:underline"
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <th className="px-3 py-3 text-left">
                <input
                  type="checkbox"
                  checked={
                    store.timeEntries.length > 0 &&
                    store.selectedTimeEntryIds.length === store.timeEntries.length
                  }
                  onChange={store.selectAllTimeEntries}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
              </th>
              <th className="px-3 py-3 text-left text-gray-600 dark:text-gray-300 font-medium">Date</th>
              <th className="px-3 py-3 text-left text-gray-600 dark:text-gray-300 font-medium">Description</th>
              <th className="px-3 py-3 text-left text-gray-600 dark:text-gray-300 font-medium">Duration</th>
              <th className="px-3 py-3 text-right text-gray-600 dark:text-gray-300 font-medium">Rate</th>
              <th className="px-3 py-3 text-right text-gray-600 dark:text-gray-300 font-medium">Amount</th>
              <th className="px-3 py-3 text-center text-gray-600 dark:text-gray-300 font-medium">Status</th>
              <th className="px-3 py-3 text-right text-gray-600 dark:text-gray-300 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {store.timeEntries.map((entry, idx) => (
              <tr
                key={entry.time_entry_id}
                className="lex-list-row hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                style={{ '--row-index': idx } as React.CSSProperties}
                onClick={() => handleEdit(entry)}
              >
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={store.selectedTimeEntryIds.includes(entry.time_entry_id)}
                    onChange={() => store.toggleTimeEntrySelection(entry.time_entry_id)}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                </td>
                <td className="px-3 py-3 text-gray-900 dark:text-white whitespace-nowrap">
                  {formatDate(entry.entry_date)}
                </td>
                <td className="px-3 py-3 text-gray-900 dark:text-white max-w-xs truncate">
                  <div className="flex items-center gap-2">
                    <span className="truncate">{entry.description}</span>
                    {entry.ai_source && entry.ai_source !== 'manual' && (
                      <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-lex-accent-soft text-lex-accent rounded">
                        AI
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 text-gray-900 dark:text-white whitespace-nowrap">
                  {formatDuration(Number(entry.duration))}
                  {entry.timer_status === 'running' && (
                    <span className="ml-1 inline-block h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                  )}
                </td>
                <td className="px-3 py-3 text-right text-gray-700 dark:text-gray-300 tabular-nums whitespace-nowrap">
                  {entry.rate != null ? formatCurrency(Number(entry.rate)) : '-'}
                </td>
                <td className="px-3 py-3 text-right text-gray-900 dark:text-white font-medium tabular-nums whitespace-nowrap">
                  {formatCurrency(Number(entry.amount))}
                </td>
                <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                  <BillingStatusBadge
                    status={entry.billing_status || 'unbilled'}
                    isNonBillable={entry.is_non_billable}
                    entityType="time-entry"
                    entityId={entry.time_entry_id}
                    onTransition={transitionTimeEntryBillingStatus}
                  />
                </td>
                <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    {/* Timer controls */}
                    {entry.timer_status === 'running' && (
                      <>
                        <button
                          onClick={() => pauseTimer(entry.time_entry_id)}
                          className="p-1 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded"
                          title="Pause timer"
                        >
                          <PauseIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => stopTimer(entry.time_entry_id)}
                          className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          title="Stop timer"
                        >
                          <StopIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    {entry.timer_status === 'paused' && (
                      <>
                        <button
                          onClick={() => startTimer(entry.time_entry_id)}
                          className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                          title="Resume timer"
                        >
                          <PlayIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => stopTimer(entry.time_entry_id)}
                          className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          title="Stop timer"
                        >
                          <StopIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    {!entry.timer_status && !entry.is_billed && (
                      <button
                        onClick={() => startTimer(entry.time_entry_id)}
                        className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                        title="Start timer"
                      >
                        <PlayIcon className="h-4 w-4" />
                      </button>
                    )}
                    {!entry.is_billed && (
                      <>
                        <button
                          onClick={() => handleEdit(entry)}
                          className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                          title="Edit"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(entry.time_entry_id)}
                          className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination info */}
      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 px-1">
        <span>
          Showing {store.timeEntries.length} of {store.timeEntryPagination.total} entries
        </span>
        {store.timeEntryPagination.has_more && (
          <button
            onClick={() => {
              const newOffset = (store.timeEntryFilters.offset || 0) + (store.timeEntryFilters.limit || 50);
              store.setTimeEntryFilters({ offset: newOffset });
            }}
            className="text-lex-accent hover:underline"
          >
            Load more
          </button>
        )}
      </div>

      <ConfirmModal
        open={deleteConfirm !== null}
        title="Delete Time Entry"
        message="This time entry will be permanently deleted. This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
};

export default TimeEntriesView;
